import { Menu } from '@headlessui/react';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';

import useLabels from '~hooks/useLabels';
import { DEFAULT_SORT_KEY, SortOption, SortOptionMap } from '~modules/media-library/model/SortOption';
import { SortOrder } from '~modules/media-library/model/SortOrder';
import { SortMenuItemButton } from '~modules/media-library/view/SortDropdown/SortMenuItemButton';

type SortMenuPanelProps = {
  options: SortOptionMap;
  sortKey: SortOption['key'];
  sortOrder: SortOrder;
  onClickOption: (sortKey: SortOption['key'], sortOrder?: SortOrder) => void;
  onToggleSortOrder: () => void;
};

export function SortMenuPanel({ options, sortKey, sortOrder, onClickOption, onToggleSortOrder }: SortMenuPanelProps) {
  // labels
  const { labelMediaLibrarySortBy, labelMediaLibrarySortOrder, labelMediaLibrarySortByDefault } = useLabels([
    'labelMediaLibrarySortBy',
    'labelMediaLibrarySortOrder',
    'labelMediaLibrarySortByDefault',
  ]);

  // Handler: click on sort option
  const makeHandleOptionClick = useCallback(
    (key: string, order?: SortOrder): ComponentProps<typeof SortMenuItemButton>['onClick'] =>
      evt => {
        // Stop event bubble because that would close the dropdown
        evt.stopPropagation();
        evt.preventDefault();

        // forward the click
        onClickOption?.(key, order);
      },
    [onClickOption],
  );

  // Handler: toggle sort order
  const handleOrderClick = useCallback<ComponentProps<typeof SortMenuItemButton>['onClick']>(
    evt => {
      // Stop event bubble because that would close the dropdown
      evt.stopPropagation();
      evt.preventDefault();

      // forward toggle click
      onToggleSortOrder?.();
    },
    [onToggleSortOrder],
  );

  // derived values
  const sortOption = options.get(sortKey);

  return (
    <div className="ml--sort-menu rounded bg-white shadow-xl flex flex-col w-full">
      <div className="flex flex-col gap-1 p-3">
        <div className="pl-1 text-darkgray font-sans text-sm leading-none select-none">{labelMediaLibrarySortBy}</div>
        <Menu.Item>
          {({ active }) => {
            return (
              <SortMenuItemButton
                focus={active}
                selected={DEFAULT_SORT_KEY === sortKey}
                onClick={makeHandleOptionClick(DEFAULT_SORT_KEY)}
              >
                {labelMediaLibrarySortByDefault}
              </SortMenuItemButton>
            );
          }}
        </Menu.Item>
        {Array.from(options.keys()).map(optionKey => (
          <Menu.Item key={optionKey}>
            {({ active }) => {
              const option = options.get(optionKey);
              return (
                <SortMenuItemButton
                  focus={active}
                  selected={option.key === sortKey}
                  onClick={makeHandleOptionClick(option.key, option.default)}
                >
                  {option.label}
                </SortMenuItemButton>
              );
            }}
          </Menu.Item>
        ))}
      </div>
      {sortOption ? (
        <div className="flex flex-col gap-1 p-3 border-solid border-gray border-t">
          <div className="pl-1 text-darkgray font-sans text-sm leading-none select-none">
            {labelMediaLibrarySortOrder}
          </div>
          <Menu.Item>
            {({ active }) => {
              return (
                <SortMenuItemButton onClick={handleOrderClick} focus={active} selected={sortOrder === SortOrder.ASC}>
                  {sortOption.asc}
                </SortMenuItemButton>
              );
            }}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => {
              return (
                <SortMenuItemButton onClick={handleOrderClick} focus={active} selected={sortOrder === SortOrder.DESC}>
                  {sortOption.desc}
                </SortMenuItemButton>
              );
            }}
          </Menu.Item>
        </div>
      ) : null}
    </div>
  );
}
